function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { autopilotHiddenHere } from "../functions/tenant-features";
import React from 'react';
import { NavLink } from 'react-router-dom';
import { hasTenantFeature, hasCampaignDashboards } from '../functions/tenant-features';
import { canViewQuotes } from '../views/sales/sales-quotes-api';
import account, { hasSection } from '../data/account';
import { getPageModules } from '../components/tenant-modules';
import { resolveBusinessModel, inventoryAvailable } from '../views/sales/territory-common';
import { getOnboardingStatus } from '../functions/onboarding-status';

/*
 * Suite nav — §363 persona/domain groups on a §366 CLICK-TO-EXPAND icon rail:
 * a single vertical column of icons (Home + Sales/Products/Marketing/Customers/
 * Tools). Clicking a group icon expands the sidebar into a second column that
 * shows ONLY that group's submenu; clicking it again / an item / the backdrop
 * collapses it. Open-group state is owned by AppSidebar and passed as props
 * (openGroup / onToggleGroup / onNavigate) — those are absent when MenuItems is
 * reused by the mobile drawer, so every use of them is guarded.
 * The group that owns the current page keeps its rail icon highlighted.
 * Persona map: VP Sales/Reps → Sales+Customers+Products · CMO/Dir Mktg →
 * Marketing+Customers · VP Customer Service → Customers · Ops VP → Products+
 * Tools · VP Technology → Tools · CEO → Home.
 *
 * Rules baked in (unchanged from §222/§363):
 *  - businessModel resolves the vertical slots (§219 vocabulary):
 *    manufacturer → Territory & Reps + Dealers · retail → Stores +
 *    Customers · both → BOTH rows (pages carry the Channel toggle).
 *  - Reports behind `customReports`; Configurations behind `productConfigs`;
 *    Campaign Review = admin/root only. Marketing/Customers rows feature-gated.
 *  - Settings · Privacy · Sign out live in the top-bar avatar menu, not here.
 */

// Which group owns a given route — used to highlight the active rail icon.
// Match rule mirrors the NavLink isActive tests: exact, or prefix + '/'.
const GROUP_ROUTES = {
  sales: ['/Sales', '/Territory', '/Dealers', '/Quotes', '/Stores', '/Forecast/Similarity'],
  products: ['/ProductPerformance', '/Forecast/Product', '/Configurations', '/Families', '/Inventory', '/Products', '/Sales/Analysis'],
  marketing: ['/Autopilot', '/MarketIntelligence', '/SiteActivity', '/Activity', '/Campaigns', '/EmailAutomation', '/Email', '/Social', '/Content', '/CampaignReview'],
  customers: ['/Customers', '/Contacts', '/Audiences', '/Lifecycle'],
  tools: ['/Data', '/Audience', '/Locations', '/Product', '/Apps', '/Research', '/Keywords', '/Seo', '/Ada', '/AiVisibility', '/Reports', '/Modules']
};
function activeGroupFor(pathname) {
  // Longest matching prefix wins ('/Sales/Analysis' is Products, '/Sales/...' is Sales).
  let best = null;
  let bestLen = -1;
  for (const key of Object.keys(GROUP_ROUTES)) {
    GROUP_ROUTES[key].forEach(r => {
      if ((pathname === r || pathname.startsWith(r + '/')) && r.length > bestLen) {
        best = key;
        bestLen = r.length;
      }
    });
  }
  return best;
}
export default class MenuItems extends React.Component {
  constructor(props) {
    super(props);
    // businessModel starts unresolved (null): the vertical slots render once resolveBusinessModel()
    // answers (boot config first, so this is immediate), never a guessed vertical.
    _defineProperty(this, "toggle", key => e => {
      e.preventDefault();
      if (this.props.onToggleGroup) this.props.onToggleGroup(key);
    });
    _defineProperty(this, "onNavigate", () => {
      if (this.props.onNavigate) this.props.onNavigate();
    });
    this.state = {
      businessModel: null,
      hasInventory: false,
      onboarding: null
    };
    this._alive = true;
  }
  componentDidMount() {
    resolveBusinessModel().then(bm => this._alive && this.setState({
      businessModel: bm
    })).catch(() => this._alive && this.setState({
      businessModel: 'both'
    }));
    // Setup entry: shown until the 7 core onboarding steps derive complete.
    getOnboardingStatus().then(ob => this._alive && this.setState({
      onboarding: ob
    })).catch(() => {/* no Setup entry if status can't be read */});
    // "Mark setup complete" / "Reopen setup" on the Setup page → drop/restore the entry live.
    this._onOnboardingChanged = () => getOnboardingStatus(true).then(ob => this._alive && this.setState({
      onboarding: ob
    })).catch(() => {});
    window.addEventListener("plunk:onboarding-changed", this._onOnboardingChanged);
    // Only the flagged tenants probe for data — the nav needs BOTH the
    // feature and a real snapshot (a flagged-but-empty feed still hides).
    if (hasTenantFeature('inventory')) {
      inventoryAvailable().then(ok => this._alive && ok && this.setState({
        hasInventory: true
      })).catch(() => {/* feed absent — nav item stays hidden */});
    }
  }
  componentWillUnmount() {
    if (this._onOnboardingChanged) window.removeEventListener("plunk:onboarding-changed", this._onOnboardingChanged);
    this._alive = false;
  }
  render() {
    var cls = "sidebar-items";
    if (this.props.cls) {
      cls = this.props.cls;
    }
    const acct = account.get() || {};
    const bm = this.state.businessModel;
    const openGroup = this.props.openGroup;
    const path = typeof window !== 'undefined' && window.location ? window.location.pathname : '/';
    const activeGroup = activeGroupFor(path);
    const groupWrap = key => "nav-group" + (openGroup === key ? " open" : "");
    const groupCls = key => "menu-group-label" + (activeGroup === key ? " active" : "");
    // Commerce sections (WORKSPACE_SECTION_ROLES.md 2026-10-23): a member sees the Sales and Marketing groups only with
    // the matching grant (account.plunk.io › Members); owners and administrators see both.
    const seeSales = hasSection('sales', acct.groups);
    const seeMarketing = hasSection('marketing', acct.groups);
    const showTerritory = hasTenantFeature('territoryRep') && (bm === 'manufacturing' || bm === 'both');
    // Sales cockpit (SALES_EXECUTIVE_VIEW.md §8): tenant feature salesTransactions plus role (owner, administrator, or the sales group).
    const salesGroups = (acct.groups || []).map(g => String(g).toLowerCase());
    const showCockpit = (hasTenantFeature('salesTransactions') || hasTenantFeature('territoryRep')) && ['owner', 'administrator', 'root', 'sales'].some(g => salesGroups.includes(g));
    // Dealer performance (review N1): the Stores rail for the wholesale book, same feature gate as Territory.
    const showDealers = showTerritory;
    const showStores = hasTenantFeature('storePerformance') && (bm === 'retail' || bm === 'both');
    // Quotes and order desk (SALES_QUOTES_ORDER_DESK.md §7): manufacturer tenants with the `quotes` feature; owners, administrators and the sales group.
    const showQuotes = hasTenantFeature('quotes') && (bm === 'manufacturing' || bm === 'both') && canViewQuotes();
    // Campaigns: the per-user Cognito claim (legacy) OR the workspace's `campaigns` feature flag —
    // Commerce workspaces get the flag at provisioning, no per-user claim needed (2026-09-27).
    const showCampaigns = Boolean(acct.showCampaigns) || hasTenantFeature('campaigns');
    // Social tenants keep Contacts even under the legacy campaigns flag —
    // SOCIAL_STATS.md §7.1 dropped the campaigns↔contacts mutual exclusion.
    const showContacts = !showCampaigns || hasTenantFeature('socialStats');
    const dealerLabel = bm === 'retail' ? 'Customers' : 'Dealers';
    // Inside the Customers group the account page is "Dealers" (wholesale) or "Accounts" (retail), never a second "Customers".
    const accountsLabel = bm === 'retail' ? 'Accounts' : 'Dealers';
    const isAdmin = account.inGroup('administrator') || account.inGroup('root');
    const showCustomers = hasTenantFeature('lifecycle') || showContacts;
    // Commerce includes Plunk Studio (DS 2026-10-27/28): the brand library and Studio open from Products for anyone whose
    // custom:apps includes studio. Plain links: one auth.plunk.io sign-in covers both apps, so Studio picks up the session.
    const hasStudio = (acct.apps || []).indexOf('studio') !== -1;
    const studioUrl = typeof window !== 'undefined' && window.location && window.location.hostname === 'staging.plunk.io' ? 'https://studio.staging.plunk.io' : 'https://studio.plunk.io';
    return /*#__PURE__*/React.createElement("div", {
      className: cls
    }, /*#__PURE__*/React.createElement(NavLink, {
      exact: true,
      to: "/",
      activeClassName: "active",
      title: "Dashboard",
      onClick: this.onNavigate
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "dashboard"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Dashboard")), seeSales && /*#__PURE__*/React.createElement("div", {
      className: groupWrap('sales')
    }, /*#__PURE__*/React.createElement("div", {
      className: groupCls('sales'),
      title: "Sales",
      onClick: this.toggle('sales')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons group-ic"
    }, "point_of_sale"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, "Sales")), /*#__PURE__*/React.createElement("div", {
      className: "nav-group-items",
      onClick: this.onNavigate
    }, /*#__PURE__*/React.createElement("div", {
      className: "nav-flyout-title"
    }, "Sales"), showCockpit && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Sales",
      activeClassName: "active",
      title: "Sales",
      isActive: (match, loc) => loc.pathname === '/Sales' || loc.pathname.startsWith('/Sales/') && !loc.pathname.startsWith('/Sales/Analysis')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "leaderboard"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Sales")), showTerritory && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Territory",
      activeClassName: "active",
      title: "Territory & Reps",
      isActive: (match, loc) => loc.pathname.startsWith('/Territory')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "travel_explore"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Territory & Reps")), showQuotes && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Quotes",
      activeClassName: "active",
      title: "Quotes",
      isActive: (match, loc) => loc.pathname.startsWith('/Quotes')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "request_quote"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Quotes")), showStores && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Stores",
      activeClassName: "active",
      title: "Stores",
      isActive: (match, loc) => loc.pathname.startsWith('/Stores')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "store"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Stores")), /*#__PURE__*/React.createElement(NavLink, {
      to: "/Forecast/Similarity",
      activeClassName: "active",
      title: "Forecasts",
      isActive: (match, loc) => loc.pathname.startsWith('/Forecast/Similarity')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "batch_prediction"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Forecasts")))), /*#__PURE__*/React.createElement("div", {
      className: groupWrap('products')
    }, /*#__PURE__*/React.createElement("div", {
      className: groupCls('products'),
      title: "Products",
      onClick: this.toggle('products')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons group-ic"
    }, "category"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, "Products")), /*#__PURE__*/React.createElement("div", {
      className: "nav-group-items",
      onClick: this.onNavigate
    }, /*#__PURE__*/React.createElement("div", {
      className: "nav-flyout-title"
    }, "Products"), /*#__PURE__*/React.createElement(NavLink, {
      to: "/Products",
      activeClassName: "active",
      title: "Product Explorer",
      isActive: (match, loc) => ['/Products', '/ProductPerformance', '/Configurations', '/Families', '/Forecast/Product'].some(p => loc.pathname === p || loc.pathname.startsWith(p + '/'))
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "explore"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Explorer")), hasTenantFeature('inventory') && this.state.hasInventory && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Inventory",
      activeClassName: "active",
      title: "Inventory",
      isActive: (match, loc) => loc.pathname.startsWith('/Inventory')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "inventory"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Inventory")), hasStudio && /*#__PURE__*/React.createElement("a", {
      href: studioUrl + '/library/brand',
      title: "Brand library (opens in Plunk Studio)"
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "photo_library"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Brand library")), hasStudio && /*#__PURE__*/React.createElement("a", {
      href: studioUrl,
      title: "Plunk Studio"
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "palette"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Plunk Studio")))), seeMarketing && /*#__PURE__*/React.createElement("div", {
      className: groupWrap('marketing')
    }, /*#__PURE__*/React.createElement("div", {
      className: groupCls('marketing'),
      title: "Marketing",
      onClick: this.toggle('marketing')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons group-ic"
    }, "ads_click"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, "Marketing")), /*#__PURE__*/React.createElement("div", {
      className: "nav-group-items",
      onClick: this.onNavigate
    }, /*#__PURE__*/React.createElement("div", {
      className: "nav-flyout-title"
    }, "Marketing"), !autopilotHiddenHere() && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Autopilot",
      activeClassName: "active",
      title: "Autopilot (beta)",
      isActive: (match, loc) => loc.pathname.startsWith('/Autopilot')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "auto_awesome"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Autopilot ", /*#__PURE__*/React.createElement("span", {
      className: "nav-beta"
    }, "Beta"))), /*#__PURE__*/React.createElement(NavLink, {
      to: "/MarketIntelligence",
      activeClassName: "active",
      title: "Market Intelligence",
      isActive: (match, loc) => loc.pathname.startsWith('/MarketIntelligence')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "leaderboard"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Market Intelligence")), /*#__PURE__*/React.createElement(NavLink, {
      to: "/SiteActivity",
      activeClassName: "active",
      title: "Website Analytics",
      isActive: (match, loc) => loc.pathname === '/SiteActivity' || loc.pathname.startsWith('/Activity')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "insights"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Website Analytics")), hasCampaignDashboards() && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Campaigns",
      activeClassName: "active",
      title: "Campaigns",
      isActive: (match, loc) => loc.pathname.startsWith('/Campaigns')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "campaign"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Campaigns")), hasTenantFeature('emailAutomation') && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Email/campaigns",
      activeClassName: "active",
      title: "Email",
      isActive: (match, loc) => loc.pathname.startsWith('/EmailAutomation') || loc.pathname === '/Email' || loc.pathname.startsWith('/Email/')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "forward_to_inbox"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Email")), hasTenantFeature('socialStats') && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Social",
      activeClassName: "active",
      title: "Social",
      isActive: (match, loc) => loc.pathname.startsWith('/Social')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "thumb_up_off_alt"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Social")), hasTenantFeature('contentHub') && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Content",
      activeClassName: "active",
      title: "Content Hub",
      isActive: (match, loc) => loc.pathname.startsWith('/Content')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "auto_stories"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Content Hub")))), showCustomers && /*#__PURE__*/React.createElement("div", {
      className: groupWrap('customers')
    }, /*#__PURE__*/React.createElement("div", {
      className: groupCls('customers'),
      title: "Customers",
      onClick: this.toggle('customers')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons group-ic"
    }, "people"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, "Customers")), /*#__PURE__*/React.createElement("div", {
      className: "nav-group-items",
      onClick: this.onNavigate
    }, /*#__PURE__*/React.createElement("div", {
      className: "nav-flyout-title"
    }, "Customers"), showContacts && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Contacts",
      activeClassName: "active",
      title: "Contacts",
      isActive: (match, loc) => loc.pathname.startsWith('/Contacts')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "person_outline"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Contacts")), hasTenantFeature('lifecycle') && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Customers",
      activeClassName: "active",
      title: accountsLabel,
      isActive: (match, loc) => loc.pathname === '/Customers' || loc.pathname.startsWith('/Customers/')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "storefront"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " ", accountsLabel)), hasTenantFeature('lifecycle') && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Lifecycle",
      activeClassName: "active",
      title: "Lifecycle",
      isActive: (match, loc) => loc.pathname.startsWith('/Lifecycle')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "filter_alt"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Lifecycle")), showContacts && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Audiences/this_month",
      activeClassName: "active",
      title: "Audiences",
      isActive: (match, loc) => loc.pathname.startsWith('/Audiences')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "groups"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Audiences")))), hasTenantFeature('customReports') && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Reports",
      activeClassName: "active",
      title: "Reports",
      onClick: this.onNavigate,
      isActive: (match, loc) => loc.pathname.startsWith('/Reports')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "summarize"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Reports")), /*#__PURE__*/React.createElement(NavLink, {
      to: "/Apps",
      activeClassName: "active",
      title: "Apps",
      onClick: this.onNavigate,
      isActive: (match, loc) => ["/Apps", "/Research", "/Keywords", "/Seo", "/Ada", "/AiVisibility"].some(r => loc.pathname === r || loc.pathname.startsWith(r + "/"))
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "apps"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Apps")), /*#__PURE__*/React.createElement(NavLink, {
      to: "/Data",
      activeClassName: "active",
      title: "Manage Data",
      onClick: this.onNavigate,
      isActive: (match, loc) => ["/Data", "/Audience", "/Locations", "/Product", "/Manage"].some(r => loc.pathname === r || loc.pathname.startsWith(r + "/"))
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "cloud_sync"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Manage Data")), this.state.onboarding && this.state.onboarding.done < this.state.onboarding.total && /*#__PURE__*/React.createElement(NavLink, {
      to: "/Setup",
      activeClassName: "active",
      title: "Finish setup",
      onClick: this.onNavigate,
      isActive: (match, loc) => ['/Setup', '/Welcome', '/Tracking'].includes(loc.pathname)
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, "rocket_launch"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " Setup"), /*#__PURE__*/React.createElement("span", {
      className: "menu-badge lbl"
    }, this.state.onboarding.done, "/", this.state.onboarding.total)), getPageModules().length > 0 && /*#__PURE__*/React.createElement("div", {
      className: groupWrap('tools')
    }, /*#__PURE__*/React.createElement("div", {
      className: groupCls('tools'),
      title: "Tools",
      onClick: this.toggle('tools')
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons group-ic"
    }, "build"), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, "Tools")), /*#__PURE__*/React.createElement("div", {
      className: "nav-group-items",
      onClick: this.onNavigate
    }, /*#__PURE__*/React.createElement("div", {
      className: "nav-flyout-title"
    }, "Tools"), getPageModules().map(m => /*#__PURE__*/React.createElement(NavLink, {
      key: m.id,
      to: '/Modules/' + m.id,
      activeClassName: "active",
      title: m.placement.menu && m.placement.menu.label || m.title || m.id
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons i-margin"
    }, m.placement.menu && m.placement.menu.icon || 'extension'), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, " ", m.placement.menu && m.placement.menu.label || m.title || m.id))))));
  }
}